import {createEntityAdapter, createSlice, EntityId} from '@reduxjs/toolkit';
import {mapValues} from 'lodash';
import {UserEntity} from '~/redux/users/types';
import {RootState} from '~/redux/root-reducer';
import {ReducerState} from '~/utils/thunk-api/type';
import {DEFAULT_SOURCE_REDUCER_STATE} from '~/utils/thunk-api/constants';
import {SOURCES_USER} from '~/redux/users/entity-config';
import {
  addIdsToSources,
  createSelectAllBySource,
  deleteSuccess,
  fetchSuccess,
  upsertMany,
  upsertOneMutably,
} from '~/utils/thunk-api';
import {addUser, deleteUser, fetchUsers, updateUser} from '~/redux/users/thunk';

/* Slice */
interface InitialState extends ReducerState {}

const sources = mapValues(SOURCES_USER, () => DEFAULT_SOURCE_REDUCER_STATE);

const usersAdapter = createEntityAdapter<UserEntity>();

const initialState = usersAdapter.getInitialState<InitialState>({
  sources,
  request: {},
});

// Slice
const users = createSlice({
  name: 'users',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchUsers.fulfilled, (state, {payload}) => {
      upsertMany(usersAdapter, state, payload.normalized.entities.users);
      fetchSuccess(state, payload);
    });
    builder.addCase(addUser.fulfilled, (state, {payload}) => {
      upsertMany(usersAdapter, state, payload.normalized.entities.users);
      addIdsToSources(state, payload.normalized.result, [SOURCES_USER.USERS_PAGE, SOURCES_USER.USERS_DROPDOWN]);
    });
    builder.addCase(updateUser.fulfilled, (state, {payload}) => {
      upsertOneMutably(usersAdapter, state, payload.normalized.entities.users);
    });
    builder.addCase(deleteUser.fulfilled, (state, {payload, meta}) => {
      const {arg} = meta;
      usersAdapter.removeOne(state, arg?.id as EntityId);
      deleteSuccess(state, arg?.id);
    });
  },
});

// export const {} = users.actions;

export default users.reducer;

export const {
  selectById: selectUserById,
  selectIds: selectUsersIds,
  selectEntities: selectUsersEntities,
  selectAll: selectAllUsers,
  selectTotal: selectTotalUsers,
} = usersAdapter.getSelectors((state: RootState) => state.users);

export const [getUsers, getDropdownUsers] = createSelectAllBySource('users', selectUsersEntities, [
  SOURCES_USER.USERS_PAGE,
  SOURCES_USER.USERS_DROPDOWN,
]);
